import { cloneDeep, sortBy } from 'lodash';
import { ReactElement, useMemo } from 'react';
import { useGetApplicationDefaultParameterSetTags } from '../../../../shared/hook/application-default-parameter-set.hooks';
import {
  ApplicationDefaultParameterSetFormTags,
  ApplicationDefaultParameterSetTagData,
} from '../../../../shared/model/flow.types';
import { BeaconWeldingData } from '../../model/beacon-welding-data';
import { WeldApplicationTagPicker } from './weld-application-tag-picker';

interface WeldApplicationSettingsTagsProps {
  process: BeaconWeldingData.WeldProcess | undefined;
  weldParameterSetFormTags?: ApplicationDefaultParameterSetFormTags;
  readOnly?: boolean;
  excludeTags?: string[];
}

export const WeldApplicationSettingsTags = (
  props: WeldApplicationSettingsTagsProps
): ReactElement => {
  const { process, weldParameterSetFormTags, excludeTags, readOnly } = props;

  const setInitialTagOptions = (
    tenantOptionPreference: ApplicationDefaultParameterSetTagData | undefined,
    equipment: string
  ): ApplicationDefaultParameterSetTagData | undefined => {
    let parameterSetTagData = cloneDeep(tenantOptionPreference);

    // temp hack to prevent race condition from resetting form values downstream by
    // hardcoding in arcal gas values
    if (
      parameterSetTagData === undefined &&
      equipment === BeaconWeldingData.Welder.OTC_WELBEE
    ) {
      parameterSetTagData = {
        gas: {
          options: [
            {
              label: 'welding:arcal_force',
              value: 'arcal_force',
              filters: [
                [
                  {
                    tag: BeaconWeldingData.ApplicationDefaultParameterSetTag
                      .PROCESS,
                    filter: ['cv', 'pulse', 'wave_pulse'],
                  },
                ],
              ],
            },
            {
              label: 'welding:arcal_flux',
              value: 'arcal_flux',
              filters: [
                [
                  {
                    tag: BeaconWeldingData.ApplicationDefaultParameterSetTag
                      .PROCESS,
                    filter: ['cv', 'pulse', 'wave_pulse'],
                  },
                ],
              ],
            },
            {
              label: 'welding:arcal_prime',
              value: 'arcal_prime',
              filters: [
                [
                  {
                    tag: BeaconWeldingData.ApplicationDefaultParameterSetTag
                      .PROCESS,
                    filter: ['cv', 'pulse', 'wave_pulse'],
                  },
                ],
              ],
            },
          ],
        },
      } as unknown as ApplicationDefaultParameterSetTagData;
    }

    return parameterSetTagData;
  };

  const weldParameterSetTags = useGetApplicationDefaultParameterSetTags({
    setInitialTagOptions,
  });

  const tagSelects = useMemo(() => {
    return sortBy(Object.entries(weldParameterSetTags), ([k, v]) => [
      v.order ?? Number.POSITIVE_INFINITY,
      k,
    ])
      .filter((tag) => {
        return !excludeTags?.map((t) => t.toString()).includes(tag[0]);
      })
      .map(([tag, data]) => {
        return (
          <WeldApplicationTagPicker
            key={tag}
            process={process}
            tagName={tag}
            tagData={data}
            tags={weldParameterSetFormTags}
            readOnly={readOnly}
          />
        );
      });
  }, [
    excludeTags,
    process,
    readOnly,
    weldParameterSetFormTags,
    weldParameterSetTags,
  ]);

  return <>{tagSelects}</>;
};
